import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext.js';
import W2Service from '../services/W2Service.js';
import { collection, query, getDocs } from 'firebase/firestore';
import { FIREBASE_DB } from '../firebase.config.js';
import { FileText, Upload, AlertCircle, Search } from 'lucide-react';

const AdminW2Upload = () => {
  const { currentUser } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadYear, setUploadYear] = useState(new Date().getFullYear());
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const employeesQuery = query(collection(FIREBASE_DB, 'users'));
      const querySnapshot = await getDocs(employeesQuery);
      const employeesList = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.role === 'employee' || data.role === 'supervisor') {
          employeesList.push({
            id: doc.id,
            ...data
          });
        }
      });
      // Sort alphabetically by name, falling back to email if name is not available
      const sortedList = employeesList.sort((a, b) => {
        const nameA = (a.name || a.email).toLowerCase();
        const nameB = (b.name || b.email).toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setEmployees(sortedList);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Failed to load employee list');
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setError(null);
    } else {
      setError('Please select a valid PDF file');
      setSelectedFile(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedEmployee) {
      setError('Please select an employee');
      return;
    }

    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess('');
      
      await W2Service.uploadW2Document(selectedEmployee.id, uploadYear, selectedFile);
      
      setSuccess(`W2 for ${selectedEmployee.name || selectedEmployee.email} (${uploadYear}) uploaded successfully`);
      setSelectedFile(null);
      // Reset file input
      const fileInput = document.getElementById('w2-file-input');
      if (fileInput) fileInput.value = '';
    } catch (err) {
      console.error('Error uploading W2:', err);
      setError('Failed to upload W2 document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const filteredEmployees = employees.filter(employee => 
    (employee.name?.toLowerCase() || employee.email.toLowerCase())
    .includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Upload W2 Documents</h1>

          {/* Employee Search */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Employee
            </label>
            <div className="relative">
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>

          {/* Employee Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Employee
            </label>
            <select
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
              value={selectedEmployee?.id || ''}
              onChange={(e) => {
                const employee = employees.find(emp => emp.id === e.target.value);
                setSelectedEmployee(employee);
              }}
            >
              <option value="">Select an employee</option>
              {filteredEmployees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name || employee.email}
                </option>
              ))}
            </select>
          </div>

          {/* Year Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tax Year
            </label>
            <input
              type="number"
              value={uploadYear}
              onChange={(e) => setUploadYear(parseInt(e.target.value))}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
              min="2000"
              max={new Date().getFullYear()}
            />
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              W2 Document (PDF)
            </label>
            <input
              id="w2-file-input"
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-light file:text-primary hover:file:bg-primary-medium"
            />
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <p className="ml-3 text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4">
              <div className="flex">
                <FileText className="h-5 w-5 text-green-400" />
                <p className="ml-3 text-sm text-green-700">{success}</p>
              </div>
            </div>
          )}

          <button
            onClick={handleUpload}
            disabled={!selectedFile || !selectedEmployee || uploading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <>
                <Upload className="animate-spin -ml-1 mr-2 h-5 w-5" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="-ml-1 mr-2 h-5 w-5" />
                Upload W2
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminW2Upload; 